import { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/feature/SEO";
import AdminLayout from "@/components/feature/AdminLayout";
import { useBrochureManager } from "@/hooks/useBrochureManager";
import {
  FileText,
  Download,
  TrendingUp,
  Eye,
  Trash2,
  Plus,
  X,
  ChevronDown,
  ChevronUp,
  Search,
  Globe,
  Printer,
  Package,
} from "lucide-react";

export default function AdminBrochures() {
  const {
    customPDFs,
    addCustomPDF,
    updateCustomPDF,
    deleteCustomPDF,
    getDownloadStats,
    getRecentDownloads,
    getTypeBreakdown,
  } = useBrochureManager();

  const [activeTab, setActiveTab] = useState<"pdfs" | "analytics">("pdfs");
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    url: "",
    category: "Comparison Guide",
    status: "active" as "active" | "inactive",
  });

  const stats = getDownloadStats();
  const recentDownloads = getRecentDownloads(100);
  const typeBreakdown = getTypeBreakdown();

  const filteredPDFs = customPDFs.filter(
    (pdf) =>
      pdf.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pdf.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.url) return;
    addCustomPDF(formData);
    setFormData({
      title: "",
      description: "",
      url: "",
      category: "Comparison Guide",
      status: "active",
    });
    setShowUploadForm(false);
  };

  const totalBrochureDownloads =
    (typeBreakdown.brochure || 0) + (typeBreakdown.all_brochures || 0);
  const totalGuideDownloads =
    (typeBreakdown.comparison_guide || 0) + (typeBreakdown.custom_pdf || 0);

  return (
    <AdminLayout>
      <SEO title="Brochures & PDFs | Admin | ExpertAdvise.in" noindex />
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        <div className="mb-6 md:mb-8">
          <h1 className="text-xl md:text-2xl font-bold text-slate-900">
            Brochures & PDFs
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage custom comparison PDFs and track visitor brochure downloads.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                <FileText className="w-4 h-4" />
              </div>
            </div>
            <div className="text-xl md:text-2xl font-bold text-slate-900">
              {customPDFs.length}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">Custom PDFs</div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <Download className="w-4 h-4" />
              </div>
            </div>
            <div className="text-xl md:text-2xl font-bold text-slate-900">
              {totalBrochureDownloads}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Brochure Downloads
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                <Printer className="w-4 h-4" />
              </div>
            </div>
            <div className="text-xl md:text-2xl font-bold text-slate-900">
              {totalGuideDownloads}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Guide Downloads
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
                <TrendingUp className="w-4 h-4" />
              </div>
            </div>
            <div className="text-xl md:text-2xl font-bold text-slate-900">
              {recentDownloads.length}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Total Tracked Events
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 bg-slate-100 p-1 rounded-xl w-fit">
          <button
            onClick={() => setActiveTab("pdfs")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer ${
              activeTab === "pdfs"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <Package className="w-4 h-4" />
            Custom PDFs
          </button>
          <button
            onClick={() => setActiveTab("analytics")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer ${
              activeTab === "analytics"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <TrendingUp className="w-4 h-4" />
            Download Analytics
          </button>
        </div>

        {activeTab === "pdfs" && (
          <div className="space-y-6">
            {/* Actions bar */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search PDFs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <button
                onClick={() => setShowUploadForm(!showUploadForm)}
                className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                {showUploadForm ? (
                  <>
                    <X className="w-4 h-4" />
                    Cancel
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Upload / Add PDF
                  </>
                )}
              </button>
            </div>

            {/* Upload Form */}
            {showUploadForm && (
              <div className="bg-white rounded-xl border border-slate-200 p-5 md:p-6">
                <h3 className="text-base font-bold text-slate-900 mb-4">
                  Add New Custom PDF
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                        Title *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.title}
                        onChange={(e) =>
                          setFormData({ ...formData, title: e.target.value })
                        }
                        placeholder="e.g. Health Insurance Comparison Guide 2025"
                        className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                        Category
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) =>
                          setFormData({ ...formData, category: e.target.value })
                        }
                        className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 bg-white"
                      >
                        <option>Comparison Guide</option>
                        <option>Brochure Summary</option>
                        <option>Lead Magnet</option>
                        <option>Policy Wordings</option>
                        <option>Other</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                      PDF URL *
                    </label>
                    <input
                      type="url"
                      required
                      value={formData.url}
                      onChange={(e) =>
                        setFormData({ ...formData, url: e.target.value })
                      }
                      placeholder="https://yourcdn.com/health-guide-2025.pdf"
                      className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Enter the direct link to the PDF file. Upload your file to
                      cloud storage and paste the public URL here.
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                      Description
                    </label>
                    <textarea
                      rows={2}
                      maxLength={500}
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      placeholder="Short description of what this PDF contains..."
                      className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 resize-none"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={formData.status === "active"}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            status: e.target.checked ? "active" : "inactive",
                          })
                        }
                        className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                      />
                      Active (visible to visitors)
                    </label>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <button
                      type="submit"
                      className="inline-flex items-center gap-2 bg-cta-600 hover:bg-cta-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors cursor-pointer"
                    >
                      <Plus className="w-4 h-4" />
                      Add PDF
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowUploadForm(false)}
                      className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium px-5 py-2.5 rounded-lg transition-colors cursor-pointer"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* PDFs Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">
                        PDF Title
                      </th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700 hidden md:table-cell">
                        Category
                      </th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700 hidden sm:table-cell">
                        Status
                      </th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700 hidden sm:table-cell">
                        Added
                      </th>
                      <th className="text-right px-4 py-3 font-semibold text-slate-700">
                        Downloads
                      </th>
                      <th className="text-right px-4 py-3 font-semibold text-slate-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredPDFs.length === 0 && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-4 py-12 text-center text-slate-500"
                        >
                          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                            <FileText className="w-5 h-5 text-slate-400" />
                          </div>
                          <p className="text-sm font-medium">
                            No custom PDFs yet
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            Click "Upload / Add PDF" to add your first
                            comparison guide or brochure summary.
                          </p>
                        </td>
                      </tr>
                    )}
                    {filteredPDFs.map((pdf) => {
                      const pdfStats = stats[pdf.title] || {
                        count: 0,
                        lastDownload: "—",
                      };
                      const isExpanded = expandedRows.has(pdf.id);
                      return (
                        <>
                          <tr
                            key={pdf.id}
                            className="hover:bg-slate-50 transition-colors"
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() => toggleRow(pdf.id)}
                                  className="w-6 h-6 rounded hover:bg-slate-200 flex items-center justify-center transition-colors cursor-pointer"
                                >
                                  {isExpanded ? (
                                    <ChevronUp className="w-4 h-4 text-slate-400" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-slate-400" />
                                  )}
                                </button>
                                <div className="min-w-0">
                                  <p className="font-medium text-slate-900 truncate">
                                    {pdf.title}
                                  </p>
                                  <p className="text-xs text-slate-400 truncate max-w-[200px] md:max-w-[300px]">
                                    {pdf.description || "No description"}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 hidden md:table-cell">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium">
                                {pdf.category}
                              </span>
                            </td>
                            <td className="px-4 py-3 hidden sm:table-cell">
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                  pdf.status === "active"
                                    ? "bg-emerald-50 text-emerald-700"
                                    : "bg-slate-100 text-slate-500"
                                }`}
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full ${
                                    pdf.status === "active"
                                      ? "bg-emerald-500"
                                      : "bg-slate-400"
                                  }`}
                                />
                                {pdf.status === "active" ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-500 hidden sm:table-cell">
                              {new Date(pdf.uploadedAt).toLocaleDateString(
                                "en-IN"
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="font-semibold text-slate-700">
                                {pdfStats.count}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <a
                                  href={pdf.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-primary-600 transition-colors"
                                  title="Open PDF"
                                >
                                  <Eye className="w-4 h-4" />
                                </a>
                                <button
                                  onClick={() => {
                                    updateCustomPDF(pdf.id, {
                                      status:
                                        pdf.status === "active"
                                          ? "inactive"
                                          : "active",
                                    });
                                  }}
                                  className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-amber-600 transition-colors cursor-pointer"
                                  title="Toggle status"
                                >
                                  <Globe className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    if (
                                      confirm(
                                        "Delete this PDF? This cannot be undone."
                                      )
                                    ) {
                                      deleteCustomPDF(pdf.id);
                                    }
                                  }}
                                  className="w-8 h-8 rounded-lg hover:bg-red-50 flex items-center justify-center text-slate-500 hover:text-red-600 transition-colors cursor-pointer"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="bg-slate-50/50">
                              <td colSpan={6} className="px-4 py-3">
                                <div className="pl-9 space-y-2">
                                  <p className="text-xs text-slate-500">
                                    <span className="font-medium">URL:</span>{" "}
                                    <a
                                      href={pdf.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-primary-600 hover:underline break-all"
                                    >
                                      {pdf.url}
                                    </a>
                                  </p>
                                  {pdfStats.count > 0 && (
                                    <p className="text-xs text-slate-500">
                                      <span className="font-medium">
                                        Last download:
                                      </span>{" "}
                                      {new Date(
                                        pdfStats.lastDownload
                                      ).toLocaleString("en-IN")}
                                    </p>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "analytics" && (
          <div className="space-y-6">
            {/* Type Breakdown */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {Object.entries(typeBreakdown).map(([type, count]) => {
                const labels: Record<string, string> = {
                  brochure: "Insurer Brochures",
                  custom_pdf: "Custom PDFs",
                  comparison_guide: "Comparison Guides",
                  all_brochures: "Bulk Downloads",
                };
                const colors: Record<string, string> = {
                  brochure: "bg-blue-50 text-blue-600",
                  custom_pdf: "bg-purple-50 text-purple-600",
                  comparison_guide: "bg-amber-50 text-amber-600",
                  all_brochures: "bg-emerald-50 text-emerald-600",
                };
                return (
                  <div
                    key={type}
                    className="bg-white rounded-xl border border-slate-200 p-4"
                  >
                    <div
                      className={`w-8 h-8 rounded-lg ${colors[type] || "bg-slate-50 text-slate-600"} flex items-center justify-center mb-2`}
                    >
                      <Download className="w-4 h-4" />
                    </div>
                    <div className="text-xl font-bold text-slate-900">
                      {count}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {labels[type] || type}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Top Downloaded Items */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 md:p-5 border-b border-slate-100">
                <h3 className="text-base font-bold text-slate-900">
                  Top Downloaded Items
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">
                        Item
                      </th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-700">
                        Type
                      </th>
                      <th className="text-right px-4 py-3 font-semibold text-slate-700">
                        Downloads
                      </th>
                      <th className="text-right px-4 py-3 font-semibold text-slate-700 hidden sm:table-cell">
                        Last Download
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {Object.entries(stats)
                      .sort((a, b) => b[1].count - a[1].count)
                      .map(([item, data]) => (
                        <tr
                          key={item}
                          className="hover:bg-slate-50 transition-colors"
                        >
                          <td className="px-4 py-3 font-medium text-slate-800 max-w-xs truncate">
                            {item}
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium capitalize">
                              {data.type.replace(/_/g, " ")}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-slate-700">
                            {data.count}
                          </td>
                          <td className="px-4 py-3 text-right text-xs text-slate-500 hidden sm:table-cell">
                            {new Date(data.lastDownload).toLocaleString(
                              "en-IN"
                            )}
                          </td>
                        </tr>
                      ))}
                    {Object.keys(stats).length === 0 && (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-4 py-12 text-center text-slate-500"
                        >
                          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                            <Download className="w-5 h-5 text-slate-400" />
                          </div>
                          <p className="text-sm font-medium">
                            No downloads tracked yet
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            Downloads from the Health Insurance Companies page
                            will appear here automatically.
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 md:p-5 border-b border-slate-100">
                <h3 className="text-base font-bold text-slate-900">
                  Recent Download Activity
                </h3>
              </div>
              <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
                {recentDownloads.slice(0, 50).map((log) => (
                  <div
                    key={log.id}
                    className="px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">
                        {log.item}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {log.type.replace(/_/g, " ")} ·{" "}
                        {new Date(log.timestamp).toLocaleString("en-IN")}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${
                        log.type === "brochure"
                          ? "bg-blue-50 text-blue-700"
                          : log.type === "comparison_guide"
                          ? "bg-amber-50 text-amber-700"
                          : log.type === "all_brochures"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-purple-50 text-purple-700"
                      }`}
                    >
                      {log.type.replace(/_/g, " ")}
                    </span>
                  </div>
                ))}
                {recentDownloads.length === 0 && (
                  <div className="px-4 py-12 text-center text-slate-500">
                    <p className="text-sm">No recent activity</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}